/**
 * Periods, and the two time formats the register has to live between.
 *
 * The events envelope carries `time` as an **ISO-8601 string**. The events *query* takes `from` and
 * `to` as **unix seconds**. Nothing converts between them today, so every caller that thinks in
 * dates has to know that, and one of them will eventually send milliseconds and get an empty
 * ledger back with no error.
 *
 * Pure. No clock - a period is always an argument, never "now". A report that reads a clock cannot
 * be re-run and `AR-RPT-03` asks for exactly that.
 *
 * Fiscal-year windows that honour a tenant setting live in `fiscal-year.js`. This file keeps the
 * ISO/unix conversion and the Australian default `financialYear` wrapper.
 */

import { yearWindowForRule } from './fiscal-year'

/** Australian financial year: 1 July to 30 June. */
export const FINANCIAL_YEAR_START_MONTH = 7

/** Unix **seconds**, which is what `validateEventsQuery` expects. Null in, null out. */
export const toUnixSeconds = value => {
    if (value == null || value === '') return null
    if (typeof value === 'number') return Number.isFinite(value) ? Math.floor(value > 1e11 ? value / 1000 : value) : null
    const ms = Date.parse(value)
    return Number.isNaN(ms) ? null : Math.floor(ms / 1000)
}

/** The date part of an ISO timestamp, so a day comparison is not a timezone argument. */
export const toIsoDate = value => {
    if (value == null || value === '') return null
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
    const ms = typeof value === 'number' ? (value > 1e11 ? value : value * 1000) : Date.parse(value)
    if (Number.isNaN(ms)) return null
    return new Date(ms).toISOString().slice(0, 10)
}

const toMs = value => (typeof value === 'number' ? (value > 1e11 ? value : value * 1000) : Date.parse(value))

/**
 * Is an event on or before an as-at? An as-at **date** means the end of that day, compared on the
 * calendar date the way `holdingBalance` does; `Date.parse('2026-09-24')` is midnight UTC at its start
 * and drops the rest of the day. An as-at with a time bounds exactly. An undated event is kept.
 */
export const onOrBeforeAsAt = (time, asAt) => {
    if (asAt == null || asAt === '') return true
    if (typeof asAt === 'string' && asAt.length <= 10) {
        const day = toIsoDate(time)
        return day === null || day <= toIsoDate(asAt)
    }
    const at = toMs(time)
    const bound = toMs(asAt)
    return Number.isNaN(at) || Number.isNaN(bound) || at <= bound
}

/**
 * The financial year *labelled* by its ending year - FY2026 is 1 Jul 2025 to 30 Jun 2026, which is
 * how an Australian accountant names it.
 *
 * `to` is the last day inclusive rather than the first day of the next year, because every
 * comparison in this module is on a date and an inclusive bound is what a reader expects a year to
 * mean.
 */
export const financialYear = (endingYear, startMonth = FINANCIAL_YEAR_START_MONTH, namedBy = 'endingYear') => {
    const year = Number(endingYear)
    if (!Number.isFinite(year)) throw new Error('financialYear: a year is required')
    return yearWindowForRule({ startMonth, namedBy }, year)
}

/** Inclusive at both ends. An absent bound is open in that direction. */
export const withinPeriod = (value, from, to) => {
    const date = toIsoDate(value)
    if (!date) return false
    const start = toIsoDate(from)
    const end = toIsoDate(to)
    if (start && date < start) return false
    if (end && date > end) return false
    return true
}

/** `from` / `to` as the events query wants them. `to` is pushed to the end of its day. */
export const toQueryWindow = ({ from, to } = {}) => ({
    ...(from != null ? { from: toUnixSeconds(`${toIsoDate(from)}T00:00:00.000Z`) } : {}),
    ...(to != null ? { to: toUnixSeconds(`${toIsoDate(to)}T23:59:59.999Z`) } : {}),
})

export default { financialYear, withinPeriod, toUnixSeconds, toIsoDate, toQueryWindow, FINANCIAL_YEAR_START_MONTH }
